import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { reapplyPenStyleToCanvas, STROKE_WIDTHS } from './signaturePadImageEngine';

function getLogicalPoint(canvas, clientX, clientY, logicalW, logicalH) {
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) {
        return { x: 0, y: 0 };
    }
    const scaleX = logicalW / rect.width;
    const scaleY = logicalH / rect.height;
    return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY,
    };
}

const SignaturePadCanvas = forwardRef(function SignaturePadCanvas({
    penColor,
    strokeWidth,
    backgroundMode,
    emptyHint,
    onStrokeChange,
}, ref) {
    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    const drawingRef = useRef(false);
    const lastPointRef = useRef(null);
    const hasStrokeRef = useRef(false);
    const sizeRef = useRef({ w: 0, h: 0 });
    const appliedPenRef = useRef({ color: penColor, width: strokeWidth });
    const [hasStroke, setHasStroke] = useState(false);

    const updateHasStroke = (next) => {
        hasStrokeRef.current = next;
        setHasStroke(next);
        onStrokeChange?.(next);
    };

    const applyPen = useCallback((ctx) => {
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = penColor;
        ctx.lineWidth = strokeWidth;
    }, [penColor, strokeWidth]);

    const resizeCanvas = useCallback(() => {
        const container = containerRef.current;
        const canvas = canvasRef.current;
        if (!container || !canvas) return;

        const logicalW = Math.max(1, Math.floor(container.clientWidth));
        const logicalH = Math.max(1, Math.floor(container.clientHeight));
        const dpr = window.devicePixelRatio || 1;
        const bufferW = Math.floor(logicalW * dpr);
        const bufferH = Math.floor(logicalH * dpr);

        if (
            sizeRef.current.w === logicalW
            && sizeRef.current.h === logicalH
            && canvas.width === bufferW
            && canvas.height === bufferH
        ) {
            return;
        }

        const snapshot = canvas.width > 0 && canvas.height > 0 && hasStrokeRef.current
            ? canvas.toDataURL('image/png')
            : null;

        sizeRef.current = { w: logicalW, h: logicalH };
        canvas.width = bufferW;
        canvas.height = bufferH;
        canvas.style.width = `${logicalW}px`;
        canvas.style.height = `${logicalH}px`;

        const ctx = canvas.getContext('2d');
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        applyPen(ctx);

        if (snapshot) {
            const img = new Image();
            img.onload = () => {
                ctx.drawImage(img, 0, 0, logicalW, logicalH);
            };
            img.src = snapshot;
        }
    }, [applyPen]);

    useEffect(() => {
        resizeCanvas();
        const container = containerRef.current;
        if (!container) return undefined;

        const observer = new ResizeObserver(() => {
            window.requestAnimationFrame(resizeCanvas);
        });
        observer.observe(container);

        const onOrient = () => window.requestAnimationFrame(resizeCanvas);
        window.addEventListener('orientationchange', onOrient);
        window.addEventListener('resize', onOrient);

        return () => {
            observer.disconnect();
            window.removeEventListener('orientationchange', onOrient);
            window.removeEventListener('resize', onOrient);
        };
    }, [resizeCanvas]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        applyPen(canvas.getContext('2d'));
    }, [applyPen]);

    const syncAppliedPen = useCallback((color, width) => {
        appliedPenRef.current = { color, width };
    }, []);

    const reapplyPenToExistingInk = useCallback(() => {
        if (drawingRef.current || !hasStrokeRef.current) return;

        const canvas = canvasRef.current;
        const { w, h } = sizeRef.current;
        if (!canvas || !w || !h) return;

        const prev = appliedPenRef.current;
        if (prev.color === penColor && prev.width === strokeWidth) return;

        const dpr = window.devicePixelRatio || 1;
        const changed = reapplyPenStyleToCanvas(
            canvas,
            w,
            h,
            dpr,
            penColor,
            strokeWidth,
            prev.color,
            prev.width,
        );

        if (changed) {
            syncAppliedPen(penColor, strokeWidth);
            applyPen(canvas.getContext('2d'));
        }
    }, [penColor, strokeWidth, applyPen, syncAppliedPen]);

    useEffect(() => {
        reapplyPenToExistingInk();
    }, [reapplyPenToExistingInk]);

    const clear = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.restore();
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        applyPen(ctx);
        syncAppliedPen(penColor, strokeWidth);
        updateHasStroke(false);
    };

    useImperativeHandle(ref, () => ({
        clear,
        isEmpty: () => !hasStrokeRef.current,
        getCanvas: () => canvasRef.current,
        getExportCanvas: () => canvasRef.current,
        loadFromFile: async (file) => {
            const canvas = canvasRef.current;
            const container = containerRef.current;
            if (!canvas || !file) return;

            const url = URL.createObjectURL(file);
            try {
                const img = await new Promise((resolve, reject) => {
                    const el = new Image();
                    el.onload = () => resolve(el);
                    el.onerror = reject;
                    el.src = url;
                });

                const width = Math.max(1, Math.floor(container?.clientWidth || canvas.clientWidth));
                const height = Math.max(1, Math.floor(container?.clientHeight || canvas.clientHeight));
                const dpr = window.devicePixelRatio || 1;

                const ctx = canvas.getContext('2d');
                ctx.save();
                ctx.setTransform(1, 0, 0, 1, 0, 0);
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.restore();

                const scale = Math.min(width / img.naturalWidth, height / img.naturalHeight);
                const dw = img.naturalWidth * scale;
                const dh = img.naturalHeight * scale;
                const dx = (width - dw) / 2;
                const dy = (height - dh) / 2;

                ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
                applyPen(ctx);
                ctx.drawImage(img, dx, dy, dw, dh);
                sizeRef.current = { w: width, h: height };
                syncAppliedPen('#000000', STROKE_WIDTHS[1]);
                updateHasStroke(true);
            } finally {
                URL.revokeObjectURL(url);
            }
        },
    }), [applyPen, penColor, strokeWidth, syncAppliedPen]);

    const drawDot = (point) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        applyPen(ctx);
        ctx.beginPath();
        ctx.arc(point.x, point.y, strokeWidth / 2, 0, Math.PI * 2);
        ctx.fillStyle = penColor;
        ctx.fill();
    };

    const drawLine = (from, to) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        applyPen(ctx);
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
        ctx.stroke();
    };

    const pointFromEvent = (e) => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const { w, h } = sizeRef.current;
        if (!w || !h) return null;
        return getLogicalPoint(canvas, e.clientX, e.clientY, w, h);
    };

    const startStroke = (point) => {
        drawingRef.current = true;
        lastPointRef.current = point;
        drawDot(point);
        if (!hasStrokeRef.current) {
            updateHasStroke(true);
        }
    };

    const continueStroke = (point) => {
        if (!drawingRef.current || !lastPointRef.current) return;
        drawLine(lastPointRef.current, point);
        lastPointRef.current = point;
    };

    const endStroke = () => {
        drawingRef.current = false;
        lastPointRef.current = null;
        syncAppliedPen(penColor, strokeWidth);
    };

    const handlePointerDown = (e) => {
        e.preventDefault();
        const canvas = canvasRef.current;
        if (!canvas) return;
        try {
            canvas.setPointerCapture(e.pointerId);
        } catch {
            /* ignore */
        }
        const point = pointFromEvent(e);
        if (!point) return;
        startStroke(point);
    };

    const handlePointerMove = (e) => {
        if (!drawingRef.current) return;
        e.preventDefault();
        const events = typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : [e];
        for (const ev of events) {
            const point = pointFromEvent(ev);
            if (point) continueStroke(point);
        }
    };

    const handlePointerUp = (e) => {
        if (!drawingRef.current) return;
        e.preventDefault();
        const canvas = canvasRef.current;
        if (canvas?.hasPointerCapture(e.pointerId)) {
            try {
                canvas.releasePointerCapture(e.pointerId);
            } catch {
                /* ignore */
            }
        }
        endStroke();
    };

    const checkerClass = backgroundMode === 'transparent'
        ? 'bg-[length:16px_16px] bg-[position:0_0,8px_8px] bg-[image:linear-gradient(45deg,#ECE8E4_25%,transparent_25%,transparent_75%,#ECE8E4_75%,#ECE8E4),linear-gradient(45deg,#ECE8E4_25%,transparent_25%,transparent_75%,#ECE8E4_75%,#ECE8E4)] dark:bg-[image:linear-gradient(45deg,#3f3f46_25%,transparent_25%,transparent_75%,#3f3f46_75%,#3f3f46),linear-gradient(45deg,#3f3f46_25%,transparent_25%,transparent_75%,#3f3f46_75%,#3f3f46)]'
        : 'bg-white dark:bg-zinc-900';

    return (
        <div
            ref={containerRef}
            className={`relative h-full min-h-0 w-full overflow-hidden rounded-xl ${checkerClass}`}
        >
            <canvas
                ref={canvasRef}
                className="block h-full w-full cursor-crosshair"
                style={{ touchAction: 'none' }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                onPointerLeave={handlePointerUp}
            />
            {!hasStroke && emptyHint ? (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-6 text-center">
                    <p className="text-[13px] font-medium text-[#A8A099] dark:text-zinc-500">
                        {emptyHint}
                    </p>
                </div>
            ) : null}
        </div>
    );
});

export default SignaturePadCanvas;
